import { Component, Input, OnInit, Injector, forwardRef } from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NgControl,
} from '@angular/forms';

export type InputType = 'text' | 'email' | 'phone';

const DEFAULT_ERRORS: Record<string, Record<string, string>> = {
  email: {
    required: 'Email is required',
    email: 'Invalid email address',
    minlength: 'Email is too short',
  },
  phone: {
    required: 'Phone number is required',
    pattern: 'Invalid phone number',
  },
  text: {
    required: 'This field is required',
    minlength: 'Value is too short',
    maxlength: 'Value is too long',
    pattern: 'Invalid format',
  },
};

@Component({
  selector: 'app-input',
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputComponent),
      multi: true,
    },
  ],
})
export class InputComponent implements ControlValueAccessor, OnInit {
  @Input() label = '';
  @Input() placeholder = '';
  @Input() inputType: InputType = 'text';
  @Input() hint = '';
  @Input() disabled = false;
  @Input() clearable = true;
  @Input() errorMessage = ''; // surcharge manuelle optionnelle
  @Input() required = false;
  value = '';

  // ── Pas de @Self() ici — on passe par l'Injector ─────────
  private ngControl: NgControl | null = null;

  constructor(private injector: Injector) {}

  ngOnInit() {
    // Résolution différée : après que les providers sont tous enregistrés
    // Cela évite la dépendance circulaire NG0200
    try {
      this.ngControl = this.injector.get(NgControl);
    } catch {
      this.ngControl = null;
    }
  }

  // ── Erreur automatique depuis le FormControl ──────────────
  get computedError(): string {
    if (this.errorMessage) return this.errorMessage;

    const ctrl = this.ngControl?.control;
    // Affiche l'erreur dès que l'utilisateur a interagi (touched ou dirty) ET que la valeur est invalide
    if (!ctrl || (!ctrl.touched && !ctrl.dirty) || ctrl.valid) return '';

    const errors = ctrl.errors;
    if (!errors) return '';

    const messages = DEFAULT_ERRORS[this.inputType] ?? DEFAULT_ERRORS['text'];

    for (const key of Object.keys(errors)) {
      if (messages[key]) return messages[key];
      if (key === 'minlength')
        return `Minimum ${errors['minlength']?.requiredLength} characters`;
      if (key === 'maxlength')
        return `Maximum ${errors['maxlength']?.requiredLength} characters`;
    }

    return 'Invalid value';
  }

  get hasError(): boolean {
    return !!this.computedError;
  }

  // ── Native type & placeholder ─────────────────────────────
  get nativeType(): string {
    if (this.inputType === 'email') return 'email';
    if (this.inputType === 'phone') return 'tel';
    return 'text';
  }

  get defaultPlaceholder(): string {
    if (this.placeholder) return this.placeholder;
    if (this.inputType === 'email') return 'you@example.com';
    if (this.inputType === 'phone') return '+1 234 567 8900';
    return 'Type here…';
  }

  // ── ControlValueAccessor ──────────────────────────────────
  private onChange: (v: string) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(val: string) {
    this.value = val ?? '';
  }
  registerOnChange(fn: (v: string) => void) {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void) {
    this.onTouched = fn;
  }
  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
  }

  onInput(event: Event) {
    this.value = (event.target as HTMLInputElement).value;
    this.onChange(this.value);
    this.onTouched(); // marque comme touché dès la saisie → active la validation
  }

  onBlur() {
    this.onTouched();
  }

  clearValue(event: Event) {
    event.stopPropagation();
    this.value = '';
    this.onChange('');
    this.onTouched();
  }
}
